import React from 'react';
import { Play, Pause, Clock, CheckCircle, XCircle, Calendar } from 'lucide-react';

const BatchProcessingTimeline = ({ schedules, onManualTrigger, processingStatus }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'paused':
        return <Pause className="w-4 h-4" />;
      case 'failed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Scheduled Batch Processing</h3>
        <div className="text-sm text-gray-600">
          <Calendar className="w-4 h-4 inline mr-1" />
          {schedules?.length || 0} Active Schedules
        </div>
      </div>
      {/* Timeline Visualization */}
      <div className="space-y-3">
        {schedules?.map((schedule) => (
          <div
            key={schedule?.id}
            className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-semibold text-gray-900">{schedule?.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(schedule?.status)}`}>
                    {getStatusIcon(schedule?.status)}
                    <span className="ml-1 capitalize">{schedule?.status}</span>
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center gap-4">
                    <span className="font-mono bg-white px-2 py-1 rounded border border-gray-200">
                      {schedule?.schedule}
                    </span>
                    <span className="text-gray-500">Timezone: {schedule?.timezone}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onManualTrigger?.(schedule?.id)}
                disabled={processingStatus === 'running'}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
              >
                <Play className="w-4 h-4" />
                Run Now
              </button>
            </div>

            {/* Execution Timeline */}
            <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200">
              <div>
                <div className="text-xs text-gray-500 mb-1">Last Run</div>
                <div className="text-sm font-medium text-gray-900">
                  {formatDate(schedule?.lastRun)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Duration: {schedule?.duration}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Next Run</div>
                <div className="text-sm font-medium text-blue-600">
                  {formatDate(schedule?.nextRun)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Success Rate</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${schedule?.successRate}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {schedule?.successRate}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {(!schedules || schedules?.length === 0) && (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No batch schedules configured</p>
        </div>
      )}
    </div>
  );
};

export default BatchProcessingTimeline;